import { useCallback, useEffect, useMemo, useState } from 'react'
import { segmentApi } from '@edulastic/api'
import { captureSentryException } from '@edulastic/common'
import {
  SEGMENT_EVENTS,
  SEGMENT_ATTACH_USER_CONFIG,
  getInsertThreshold,
  buildSatisfactionSegmentPayload,
  isSearchSatisfactionGloballyDismissed,
  setSearchSatisfactionGloballyDismissed,
} from './constants'

const trackSatisfactionEvent = (eventName, payload) => {
  try {
    segmentApi.genericEventTrack(eventName, payload, SEGMENT_ATTACH_USER_CONFIG)
  } catch (e) {
    captureSentryException(e, {
      errorMessage: `SearchSatisfaction: error tracking ${eventName}`,
    })
  }
}

const useSearchSatisfactionBanner = ({
  searchFingerprint,
  itemCount,
  viewMode = 'list',
  gridCountInARow = 3,
  libraryType,
  surface,
  searchId = '',
  filter = {},
  items = [],
  isGcpsDistrict = false,
}) => {
  const [activeVote, setActiveVote] = useState(null)
  const [dismissedLocally, setDismissedLocally] = useState(false)
  const kid = window.sessionStorage?.kid || ''

  const threshold = useMemo(
    () => getInsertThreshold(viewMode, gridCountInARow),
    [viewMode, gridCountInARow]
  )

  const isGloballyDismissed = useMemo(
    () => dismissedLocally || isSearchSatisfactionGloballyDismissed(kid),
    [kid, dismissedLocally]
  )

  useEffect(() => {
    setActiveVote(null)
  }, [searchFingerprint])

  const segmentPayload = useMemo(
    () =>
      buildSatisfactionSegmentPayload({
        libraryType,
        surface,
        searchId,
        filter,
        items,
        threshold,
      }),
    [libraryType, surface, searchId, filter, items, threshold]
  )

  const meetsThreshold = itemCount >= threshold
  const shouldInsertBanner =
    meetsThreshold && !isGloballyDismissed && !isGcpsDistrict

  const onThumbsUp = useCallback(() => {
    setActiveVote('up')
    trackSatisfactionEvent(
      SEGMENT_EVENTS.SATISFACTION_THUMBS_UP,
      segmentPayload
    )
  }, [segmentPayload])

  const onThumbsDown = useCallback(() => {
    setActiveVote('down')
    trackSatisfactionEvent(
      SEGMENT_EVENTS.SATISFACTION_THUMBS_DOWN,
      segmentPayload
    )
  }, [segmentPayload])

  const onDismiss = useCallback(() => {
    setSearchSatisfactionGloballyDismissed(kid)
    setDismissedLocally(true)
    trackSatisfactionEvent(SEGMENT_EVENTS.SATISFACTION_DISMISS, segmentPayload)
  }, [kid, segmentPayload])

  return {
    shouldInsertBanner,
    activeVote,
    onThumbsUp,
    onThumbsDown,
    onDismiss,
    threshold,
  }
}

export default useSearchSatisfactionBanner
